import React from "react";
import emailjs from "emailjs-com";
import { toast } from "react-toastify";
import { ArrowUpRight, Mail, MapPin, Clock } from "lucide-react";
import { JHALA_GLOBAL_CSS, TopStrip, Header, Footer, Reveal, TagChip } from "components/jhala/Layout.js";

const inputStyle = {
  width: "100%",
  border: "none",
  borderBottom: "2px solid #DCD8CC",
  padding: "12px 2px",
  fontSize: 14,
  fontFamily: "'Inter', sans-serif",
  background: "transparent",
  color: "#0A0F1E",
  outline: "none",
  boxSizing: "border-box",
};

const CONTACT_INFO = [
  { icon: Mail, label: "Email", value: "sales@jhalatech.com", href: "mailto:sales@jhalatech.com" },
  { icon: Clock, label: "Response time", value: "Usually within 1 business day", href: null },
  { icon: MapPin, label: "Based in", value: "Indore, Madhya Pradesh, India", href: null },
];

export default function ContactUsPage() {
  const handleSubmit = (e) => {
    e.preventDefault();

    const SERVICE_ID = "service_xzlcvo1";
    const TEMPLATE_ID = "template_caupis8";
    const USER_ID = "XlRMBTadMyxn2irk2";

    emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, e.target, USER_ID)
      .then(() => {
        toast.success("Your email has been forwarded to our support team. They will contact you soon via the same email.");
      }, () => {
        toast.error("Failed to send mail address. Please try again.");
      });

    e.target.reset();
  };

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: "#F6F4EE", color: "#0A0F1E" }}>
      <style>{JHALA_GLOBAL_CSS}</style>
      <TopStrip />
      <Header active="Contact" />

      {/* HERO */}
      <section style={{ background: "#0A0F1E", color: "#F6F4EE", padding: "64px 24px 56px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(#23304A 1px, transparent 1px), linear-gradient(90deg, #23304A 1px, transparent 1px)", backgroundSize: "48px 48px", opacity: 0.2, maskImage: "radial-gradient(ellipse at 70% 30%, black, transparent 70%)" }} />
        <div style={{ maxWidth: 1180, margin: "0 auto", position: "relative" }}>
          <Reveal>
            <TagChip>$ jhalatech --contact</TagChip>
            <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(30px, 4.4vw, 46px)", lineHeight: 1.1, margin: "18px 0 14px" }}>
              Let's talk about your project.
            </h1>
            <p style={{ fontSize: 16, color: "#B8BFCF", maxWidth: 560, lineHeight: 1.65 }}>
              Fill out the form and we'll get back to you shortly — or reach us directly using the details below.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FORM + INFO */}
      <section style={{ padding: "80px 24px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: 56 }} className="contact-grid">
          <Reveal>
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 480 }}>
              <div>
                <label style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "#8B93A7", display: "block", marginBottom: 6 }}>FULL NAME</label>
                <input type="text" name="name" placeholder="Your name" required style={inputStyle} />
              </div>
              <div>
                <label style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "#8B93A7", display: "block", marginBottom: 6 }}>EMAIL ADDRESS</label>
                <input type="email" name="email" placeholder="you@company.com" required style={inputStyle} />
              </div>
              <div>
                <label style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "#8B93A7", display: "block", marginBottom: 6 }}>SUBJECT</label>
                <input type="text" name="subject" placeholder="What's this about?" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "#8B93A7", display: "block", marginBottom: 6 }}>MESSAGE</label>
                <textarea name="message" placeholder="Tell us about your project..." required rows={5} style={{ ...inputStyle, resize: "vertical", fontFamily: "'Inter', sans-serif" }} />
              </div>
              <button type="submit" className="cta-btn" style={{ background: "#FF5C39", color: "#0A0F1E", border: "none", padding: "13px 24px", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, alignSelf: "flex-start" }}>
                Send Message <ArrowUpRight size={16} />
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15}>
            <div style={{ background: "#0A0F1E", borderRadius: 10, padding: "32px 28px" }}>
              <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 17, fontWeight: 600, color: "#F6F4EE", margin: "0 0 22px" }}>
                Other ways to reach us
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
                {CONTACT_INFO.map((c) => (
                  <div key={c.label} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <c.icon size={18} style={{ color: "#4CE0B3", marginTop: 2, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "#8B93A7", marginBottom: 3 }}>{c.label.toUpperCase()}</div>
                      {c.href ? (
                        <a href={c.href} style={{ color: "#F6F4EE", fontSize: 14.5, textDecoration: "none" }}>{c.value}</a>
                      ) : (
                        <div style={{ color: "#F6F4EE", fontSize: 14.5 }}>{c.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
